import React from 'react';
import Konva from 'konva';
import { Group, Line as LineElement } from 'react-konva';
import { KonvaEventObject } from 'konva/lib/Node';

import {
  getPointerClickPosition,
  getPointerPosition,
} from '../services/get_pointer_position';
import { coordinatesToPairs } from '../services/coordinates_to_pairs';
import { ROI_CONFIG, HIGHLIGHT_COLOR } from '../config';
import { ROIAnchorPoint } from './AnchorPoint';
import { ROILabel } from './Label';

function findClosestSegmentInsertIndex(
  points: number[],
  [px, py]: readonly [number, number],
  closed: boolean
): number | null {
  const vertexCount = points.length / 2;
  if (vertexCount < 2) {
    return null;
  }

  let bestDist2 = Number.POSITIVE_INFINITY;
  let bestStartVertex: number | null = null;

  const segmentCount = closed ? vertexCount : vertexCount - 1;

  for (let start = 0; start < segmentCount; start += 1) {
    const end = closed ? (start + 1) % vertexCount : start + 1;

    const ax = points[start * 2];
    const ay = points[start * 2 + 1];
    const bx = points[end * 2];
    const by = points[end * 2 + 1];

    const abx = bx - ax;
    const aby = by - ay;
    const apx = px - ax;
    const apy = py - ay;

    const abLen2 = abx * abx + aby * aby;
    if (abLen2 === 0) {
      continue;
    }

    let t = (apx * abx + apy * aby) / abLen2;
    t = Math.max(0, Math.min(1, t));

    const cx = ax + t * abx;
    const cy = ay + t * aby;
    const dx = px - cx;
    const dy = py - cy;
    const dist2 = dx * dx + dy * dy;

    if (dist2 < bestDist2) {
      bestDist2 = dist2;
      bestStartVertex = start;
    }
  }

  if (bestStartVertex === null) {
    return null;
  }

  // If we clicked the closing segment (last -> first), insert at the end.
  if (closed && bestStartVertex === vertexCount - 1) {
    return points.length;
  }

  // Insert after the segment start vertex.
  return (bestStartVertex + 1) * 2;
}

export type ROIShapeProps = {
  labels: string[];
  label: string;
  shape: number[];
  closed?: boolean;
  selectedAnchor: Konva.Circle | null;
  isSelected?: boolean;
  isShowingLabels?: boolean;
  onChange: (label: string, shape: number[]) => void;
  onRename: (oldLabel: string, newLabel: string) => void;
  onSelect: (label: string) => void;
  onSelectedAnchorChange: (anchor: Konva.Circle | null) => void;
  onDelete: (label: string) => void;
};

export function ROIShape({
  labels,
  label,
  shape,
  closed,
  selectedAnchor,
  isSelected,
  isShowingLabels,
  onChange,
  onRename,
  onSelect,
  onSelectedAnchorChange,
  onDelete,
}: ROIShapeProps) {
  const groupRef = React.useRef<Konva.Group>(null);
  const shapeRef = React.useRef<Konva.Line>(null);
  const mirrorRef = React.useRef<Konva.Line>(null);
  const anchorRefs = React.useRef<(Konva.Circle | null)[]>([]);

  const [isHovering, setIsHovering] = React.useState(false);

  const [anchorPoints, setAnchorPoints] = React.useState(
    coordinatesToPairs(shape)
  );

  React.useLayoutEffect(() => {
    setAnchorPoints(coordinatesToPairs(shape));
  }, [shape]);

  React.useEffect(() => {
    if (!isSelected) {
      return;
    }

    // If multiple ROIs overlap, anchors of earlier-drawn shapes can be
    // unclickable because they're under later-drawn shapes. When a shape is
    // selected, bring it (and its anchors) to the front.
    groupRef.current?.moveToTop();
    groupRef.current?.getLayer()?.batchDraw();
  }, [isSelected]);

  function handleMouseEnter() {
    setIsHovering(true);
  }

  function handleMouseLeave() {
    setIsHovering(false);
  }

  function handleAnchorMove(event: KonvaEventObject<DragEvent>) {
    const shape = shapeRef.current;
    const anchors = anchorRefs.current;

    if (!shape || !anchors) {
      return;
    }

    const anchorPos = getPointerPosition(shape.getStage());

    if (anchorPos) {
      event.target.x(anchorPos.x);
      event.target.y(anchorPos.y);
    }

    const newShape = shape
      .points(
        anchors.flatMap((anchor) => {
          if (!anchor) {
            return [];
          }

          return [anchor.x(), anchor.y()];
        })
      )
      .points();

    onChange(label, newShape);
  }

  function handleAnchorSelect(
    event: KonvaEventObject<MouseEvent>,
    index: number
  ) {
    event.cancelBubble = true;

    const anchor = anchorRefs.current[index];

    if (!anchor) {
      return;
    }

    onSelectedAnchorChange(anchor);
  }

  function handleLabelChange(newLabel: string) {
    onRename(label, newLabel);
  }

  function handleClick(event: KonvaEventObject<MouseEvent>) {
    event.cancelBubble = true;

    // First click selects; subsequent clicks on the stroke insert a new point.
    if (!isSelected) {
      onSelect(label);
      return;
    }

    const stage = shapeRef.current?.getStage() ?? event.target.getStage();
    const pointerPos = getPointerClickPosition(stage);

    if (!pointerPos) {
      return;
    }

    const points = shapeRef.current?.points() ?? shape;
    const insertIdx = findClosestSegmentInsertIndex(
      points,
      [pointerPos.x, pointerPos.y],
      Boolean(closed)
    );

    if (insertIdx === null) {
      return;
    }

    const nextShape = [
      ...points.slice(0, insertIdx),
      pointerPos.x,
      pointerPos.y,
      ...points.slice(insertIdx),
    ];

    onChange(label, nextShape);
  }

  // Stage event listeners to delete shapes or anchors
  React.useEffect(() => {
    const shape = shapeRef.current;
    const stage = shape?.getStage();
    const container = stage?.container();

    if (!shape || !container) {
      return;
    }

    function handleStageKeyDown(event: KeyboardEvent) {
      if (!shape) {
        return;
      }

      switch (event.key) {
        case 'Backspace':
        case 'Delete': {
          // Delete shape
          if (isSelected) {
            onDelete(label);
            shape.destroy();
            return;
          }

          // Delete selected anchor
          if (selectedAnchor) {
            const ax = selectedAnchor.x();
            const ay = selectedAnchor.y();

            const updatedShape = shape
              .points(
                anchorPoints.filter(([bx, by]) => ax !== bx || ay !== by).flat()
              )
              .points();

            onChange(label, updatedShape);
            onSelectedAnchorChange(null);
          }
        }
      }
    }

    container.addEventListener('keydown', handleStageKeyDown);

    return () => {
      container.removeEventListener('keydown', handleStageKeyDown);
    };
  }, [
    anchorPoints,
    label,
    isSelected,
    selectedAnchor,
    onDelete,
    onChange,
    onSelectedAnchorChange,
  ]);

  const mirrorPoints = shape;
  let mirrorStrokeWidth = isHovering ? 1 : 0;

  if (isSelected) {
    mirrorStrokeWidth = 1.5;
  }

  return (
    <Group ref={groupRef}>
      <LineElement
        {...ROI_CONFIG.line}
        points={shape}
        closed={closed}
        ref={shapeRef}
      />

      {/* Mirror to handle events & mouseover indicator.
          For polygons, we enable an invisible fill so the whole area is clickable. */}
      <LineElement
        points={mirrorPoints}
        closed={closed}
        hitStrokeWidth={ROI_CONFIG.line.hitStrokeWidth}
        strokeWidth={mirrorStrokeWidth}
        stroke={HIGHLIGHT_COLOR}
        {...(closed && {
          fill: `${HIGHLIGHT_COLOR}${isSelected ? '33' : '00'}`,
        })}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        ref={mirrorRef}
      />

      {anchorPoints.map(([x, y], i) => (
        <ROIAnchorPoint
          x={x}
          y={y}
          key={i}
          onDragMove={handleAnchorMove}
          onClick={(event) => handleAnchorSelect(event, i)}
          ref={(el) => (anchorRefs.current[i] = el)}
          isSelected={selectedAnchor === anchorRefs.current[i]}
          draggable
        />
      ))}

      {isShowingLabels && (
        <ROILabel
          labels={labels}
          text={label}
          shape={shape}
          closed={closed}
          onChange={handleLabelChange}
        />
      )}
    </Group>
  );
}
